import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    Select,
    MenuItem,
    FormControl,
    OutlinedInput,
    Chip,
    Divider,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AuthContext } from '../../../../Helpers/Contexts/authContext';
import { AuthUser } from '../../../../interfaces/Contexts';
import { createFolder, getCollaborators } from '../../../../Utils/apiService';
import { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import KenLoader from '../../../../GlobalComponents/KenLoader';
import { enqueueSnackbar } from 'notistack';

interface CollaboratorType {
    Id: string;
    Name: string;
    Email: string;
    Profile_Picture_URL__c?: string;
}

interface CreateAlbumModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const useStyles = makeStyles((theme: any) => ({
    gradientButton: {
        background: 'linear-gradient(90deg, #00387A -30.21%, #006BB7 100%)',
        color: '#ffffff',
        textTransform: 'none',
        borderRadius: '4px',
        padding: '6px 16px',
        fontWeight: 500,
        fontSize: '14px',
        '&:hover': {
            background: 'linear-gradient(90deg, #002B5E -30.21%, #005A9E 100%)',
        },
        '&.Mui-disabled': {
            background: 'linear-gradient(90deg, #00387A -30.21%, #006BB7 100%)',
            opacity: 0.7,
            color: '#ffffff',
        }
    },
    dialogTitle: {
        padding: '16px 24px',
        '& .MuiTypography-root': {
            fontSize: '18px',
            fontWeight: 500,
        }
    },
    formLabel: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: 500,
        fontSize: '14px',
    },
    borderedSelect: {
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#B2B3B7',
            borderWidth: '0.5px',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#B2B3B7',
            borderWidth: '0.5px',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#B2B3B7',
            borderWidth: '0.5px',
        },
        height: '40px',
    },
    borderedTextField: {
        '& .MuiOutlinedInput-root': {
            height: '40px',
            '& fieldset': {
                borderColor: '#B2B3B7',
                borderWidth: '0.5px',
            },
            '&:hover fieldset': {
                borderColor: '#B2B3B7',
                borderWidth: '0.5px',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#B2B3B7',
                borderWidth: '0.5px',
            },
        },
    },
    imageUploadBox: {
        height: '130px',
        background: '#E8EBF3',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer'
    },
    coverPreview: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    uploadIcon: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '8px'
    },
    cancelButton: {
        color: 'black',
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '14px'
    },
    hiddenInput: {
        display: 'none'
    }
}));

const CreateAlbumModal: React.FC<CreateAlbumModalProps> = ({ open, onClose, onSuccess }) => {
    const classes = useStyles();
    const [albumName, setAlbumName] = useState<string>('');
    const [collaborators, setCollaborators] = useState<CollaboratorType[]>([]);
    const [selectedCollaborators, setSelectedCollaborators] = useState<string[]>([]);
    const [coverImage, setCoverImage] = useState<File | null>(null);
    const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
    const [coverImageBase64, setCoverImageBase64] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [collaboratorsLoading, setCollaboratorsLoading] = useState<boolean>(false); // <-- new state

    const { state: { user } } = useContext(AuthContext);
    const userData = user as AuthUser;

    useEffect(() => {
        if (open) {
            fetchCollaborators();
            setAlbumName('');
            setSelectedCollaborators([]);
            setCoverImage(null);
            setCoverImagePreview(null);
            setCoverImageBase64(null);
        }
    }, [open]);

    const fetchCollaborators = async () => {
        setCollaboratorsLoading(true);
        try {
            const response = await getCollaborators(userData?.applicantId || '');
            if (response && response.data && response.data.data) {
                setCollaborators(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching collaborators:", error);
        } finally {
            setCollaboratorsLoading(false);
        }
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const selectedFile = files[0];
            if (selectedFile.size > 1 * 1024 * 1024) { // 1MB
                enqueueSnackbar('Image should be less than 1 MB', { variant: 'error' });
                return;
            }
            const acceptedFormats = ['image/png', 'image/jpeg', 'image/jpg'];
            if (!acceptedFormats.includes(selectedFile.type)) {
                enqueueSnackbar('Please upload an image in PNG, JPEG, or JPG format', { variant: 'error' });
                return;
            }
            setCoverImage(selectedFile);
            const reader = new FileReader();
            reader.onloadend = () => {
                setCoverImagePreview(reader.result as string);
            };
            reader.readAsDataURL(selectedFile);
            try {
                const base64 = await convertFileToBase64(selectedFile);
                setCoverImageBase64(base64);
            } catch (error) {
                console.error("Error converting image to base64:", error);
                alert('Failed to process the image. Please try again.');
            }
        }
    };

    const convertFileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result === 'string') {
                    const base64String = reader.result.split(',')[1];
                    resolve(base64String);
                } else {
                    reject(new Error('Failed to convert file to base64'));
                }
            };
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    };

    const handleSubmit = async () => {
        if (!albumName.trim() || !coverImage) {
            alert('Album name and cover image are required');
            return;
        }
        setLoading(true);
        try {
            const payload = {
                folderName: albumName.trim(),
                contactId: userData?.applicantId || '',
                collaboratorIds: selectedCollaborators,
                folderType: 'Album',
                coverImage: {
                    fileName: coverImage.name,
                    fileBlob: coverImageBase64
                }
            };

            const response = await createFolder(payload as any);
            if (response && !response.error) {
                onSuccess();
                handleClose();
            } else {
                console.error("Error creating album:", response.error);
                alert('Failed to create album. Please try again.');
            }
        } catch (error) {
            console.error("Error creating album:", error);
            alert('An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setAlbumName('');
        setSelectedCollaborators([]);
        setCoverImage(null);
        setCoverImagePreview(null);
        setCoverImageBase64(null);
        onClose();
    };

    const isFormValid = albumName.trim() !== '' && coverImage !== null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle className={classes.dialogTitle}>
                <Typography variant="h6">Create new album</Typography>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ padding: '24px' }}>
                <Box sx={{ mb: 3 }}>
                    <Typography component="label" htmlFor="album-name" sx={{ fontWeight: "500", fontSize: "16px", marginBottom: "5px" }}>
                        Album name<span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <TextField
                        id="album-name"
                        placeholder="Name your album"
                        fullWidth
                        value={albumName}
                        onChange={(e) => setAlbumName(e.target.value)}
                        size="small"
                        className={classes.borderedTextField}
                    />
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography component="label" htmlFor="collaborators" sx={{ fontWeight: "500", fontSize: "16px", marginBottom: "5px" }}>
                        Add collaborators <Typography component="span" color="text.secondary" sx={{ fontWeight: 400, fontSize: "14px" }}>(Optional)</Typography>
                    </Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            multiple
                            displayEmpty
                            value={selectedCollaborators}
                            onChange={(e) => setSelectedCollaborators(e.target.value as string[])}
                            input={<OutlinedInput />}
                            IconComponent={KeyboardArrowDownIcon}
                            className={classes.borderedSelect}
                            renderValue={(selected: any) => {
                                if (selected.length === 0) {
                                    return <Typography color="text.secondary">Choose collaborators</Typography>;
                                }

                                return (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value: any) => {
                                            const collaborator = collaborators.find(c => c.Id === value);
                                            return (
                                                <Chip
                                                    key={value}
                                                    label={collaborator ? collaborator.Name : value}
                                                    size="small"
                                                />
                                            );
                                        })}
                                    </Box>
                                );
                            }}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 224,
                                    },
                                },
                            }}
                        >
                            {collaboratorsLoading ? (
                                <MenuItem disabled>
                                    <KenLoader />
                                    <Typography sx={{ marginLeft: '10px' }}>Loading...</Typography>
                                </MenuItem>
                            ) : (
                                collaborators.length > 0 ? (
                                    collaborators.map((collaborator) => (
                                        <MenuItem
                                            key={collaborator.Id}
                                            value={collaborator.Id}
                                        >
                                            {collaborator.Name}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>
                                        <Typography>No collaborators</Typography>
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </FormControl>
                </Box>

                <Box>
                    <Box
                        className={classes.imageUploadBox}
                        onClick={() => document.getElementById('cover-image-input')?.click()}
                    >
                        {coverImagePreview ? (
                            <img
                                src={coverImagePreview}
                                alt="Cover preview"
                                className={classes.coverPreview}
                            />
                        ) : (
                            <>
                                <Box component="span" className={classes.uploadIcon}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="#0B3E69" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M17 8L12 3L7 8" stroke="#0B3E69" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M12 3V15" stroke="#0B3E69" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </Box>
                                <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                                    Upload cover image
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: '12px', fontWeight: 400 }}>
                                    Max image size: 1 MB | Min width: 1028px
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 400 }}>
                                    Image format: PNG, JPEG, JPG
                                </Typography>
                            </>
                        )}
                        <input
                            type="file"
                            id="cover-image-input"
                            accept="image/png, image/jpeg, image/jpg"
                            className={classes.hiddenInput}
                            onChange={handleImageUpload}
                        />
                    </Box>
                </Box>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button
                    onClick={handleClose}
                    className={classes.cancelButton}
                >
                    Cancel
                </Button>
                <Button
                    disabled={loading || !isFormValid}
                    onClick={handleSubmit}
                    className={classes.gradientButton}
                    sx={{ color: "#ffffff" }}
                >
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateAlbumModal;